import React, { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CircuitList } from "components/inputs/circuits/CircuitList";
import { OptionBar } from "components/inputs/OptionBar";
import { Popup, PopupProps } from "components/popups/Popup";
import { buildZone1ValidationSchema } from "components/EtudeForm/constants";
import Button from "components/shared/button";
import Input, { InputType } from "components/shared/inputs/input";
import { Text } from "components/shared/text";
import { SolutionRadio } from "components/inputs/SolutionRadio";
import { InstallationType } from "components/EtudeForm/hooks/useGetSolutionsQuery";
import { useCurrentEtudeData } from "components/EtudeForm/hooks/useCurrentEtudeData";
import { usePopup } from "providers/PopupProvider";
import { setEtudeData } from "store/etudeSlice";
import { useAppDispatch } from "store/hooks";
import { SystemCode } from "utils/engine/consts";
import { getZone1Data } from "utils/engine/engine";
import useTranslation from "utils/hooks/useTranslation";
import valeurParDefaut from "utils/engine/valeurParDefaut.json";

interface ModifyParamsFormValues {
    t_base: number | null;
    TcoupureQuestion: boolean;
    Tcoupure: number | null;
    t_ete: number | null;
    distance_pac_ballon: number | null;
    distance_pac_batiment: number | null;
    antiCorrosion: "yes" | "no";
    boilerForEcs?: "yes" | "no" | null;
    circuits: Array<{
        id: string;
        name?: string;
        underfloorHeating?: "yes" | "no" | null;
        type?: number;
        puissance?: number | null;
        startTemperature?: number | null;
        delta_t?: number | null;
        surpuissance?: number | null;
    }>;
    etudeFormSolutionId: string;
    etudeFormSolutions?: EtudeSolution[];
}

export const ModifyParamsPopup: React.FC<PopupProps> = (props) => {
    const { t } = useTranslation();
    const { json, etudeData, solutionRequestBody, installationType, service, hasEcs, hasChauffage } =
        useCurrentEtudeData();
    const dispatch = useAppDispatch();
    const { closeLastPopup } = usePopup();

    const isHybride = installationType === InstallationType.hybride;
    const isDoubleServiceHybrid = isHybride && hasEcs && hasChauffage;

    const validationSchema = useMemo(() => {
        const circuits = json?.circuits ?? [];
        const zone1Data = getZone1Data(installationType, json?.city, circuits);
        return yup.object().shape(
            buildZone1ValidationSchema({
                circuits,
                requireByService: false,
                t,
                zone1Data,
            })
        );
    }, [installationType, json?.city, json?.circuits, t]);

    const { control, watch, handleSubmit, reset: _reset } = useForm<ModifyParamsFormValues>({
        defaultValues: {
            t_base: json?.t_base ?? solutionRequestBody?.t_base ?? null,
            TcoupureQuestion: json?.TcoupureQuestion ?? false,
            Tcoupure: json?.Tcoupure ?? null,
            t_ete: json?.t_ete ?? (solutionRequestBody as any)?.t_ete ?? null,
            distance_pac_ballon:
                json?.distance_pac_ballon ?? solutionRequestBody?.distance_pac_ballon ?? null,
            distance_pac_batiment:
                json?.distance_pac_batiment ?? solutionRequestBody?.distance_pac_batiment ?? null,
            antiCorrosion:
                json?.antiCorrosion ??
                (solutionRequestBody?.traitement_anti_corrosion ? "yes" : "no"),
            boilerForEcs: json?.boilerForEcs ?? null,
            circuits: json?.circuits ?? [],
            etudeFormSolutionId: json?.etudeFormSolutionId?.toString() ?? "0",
            etudeFormSolutions: json?.calculatedSolutions ?? [],
        },
        resolver: yupResolver(validationSchema) as any,
        mode: "onChange",
    });

    const tcoupureQuestion = watch("TcoupureQuestion");
    const etudeFormSolutions = watch("etudeFormSolutions") || [];
    // // Fetch ECS solutions when popup opens (for ECS types)
    // useEffect(() => {
    //     if (!hasEcs || !network || !json?.systeme) return;

    //     const data: Record<string, any> = {
    //         batiment: solutionRequestBody?.circuit_ecs?.batiment,
    //         t_eau_chaude: json?.TECS,
    //         temperature_primaire: json?.Tp,
    //         mode: "SI/SA",
    //         systeme: json?.systeme,
    //         reseau_commercial: network?.codeReseauCommercial,
    //     };

    //     if (json?.Pboucle !== undefined && json?.Pboucle !== null) {
    //         data.pertes_thermiques_bouclage = String(json.Pboucle);
    //     }

    //     setIsLoadingEcs(true);
    //     api
    //         .post("moteur/ecs/solution", data)
    //         .then((res: any) => {
    //             if (res.data?.solutions) {
    //                 setEcsSolutions(res.data.solutions);
    //             }
    //         })
    //         .catch(() => { })
    //         .finally(() => setIsLoadingEcs(false));
    // }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onValidate = useCallback((values: ModifyParamsFormValues) => {
        const traitement_anti_corrosion = values.antiCorrosion === "yes";

        // Build circuits for request body
        const circuitsForRequest =
            hasChauffage && values.circuits?.length
                ? values.circuits
                    .filter((c) => c.id)
                    .map((c) => ({
                        type: c.type,
                        puissance: c.puissance,
                        consigne_depart: c.startTemperature,
                        delta_t: c.delta_t,
                        plancher_chauffant: c.underfloorHeating === "yes",
                        surpuissance: c.surpuissance,
                    }))
                : solutionRequestBody?.circuits;

        // Update circuit_ecs if ECS solution changed
        let updatedCircuitEcs = solutionRequestBody?.circuit_ecs;
        if (hasEcs && etudeFormSolutions.length > 0 && values.etudeFormSolutionId !== undefined) {
            const selectedSolution = etudeFormSolutions[+values.etudeFormSolutionId];
            if (selectedSolution) {
                const refType =
                    json?.systeme === SystemCode.ECHANGEUR_BALLONS_ECS ? "exchange" : "stockprep";
                updatedCircuitEcs = {
                    ...updatedCircuitEcs,
                    code_ballon_prep: selectedSolution.references_commerciales?.find(
                        (r: any) => r.type === refType
                    )?.code,
                    codes_ballon_stockage_ecs: selectedSolution.references_commerciales
                        ?.filter((r: any) => r.type === "stockecs")
                        .map((r: any) => r.code),
                    puissance_optimisee: selectedSolution.puissance_optimisee,
                    puissance_reelle: selectedSolution.puissance_reelle,
                };
            }
        }

        const newSolutionRequestBody: GetSolutionRequest = {
            ...solutionRequestBody!,
            t_base: values.t_base,
            t_ete: values.t_ete,
            distance_pac_ballon: values.distance_pac_ballon,
            distance_pac_batiment: values.distance_pac_batiment,
            traitement_anti_corrosion,
            t_coupure: values.TcoupureQuestion ? values.Tcoupure : null,
            circuits: circuitsForRequest,
            circuit_ecs: updatedCircuitEcs,
            ...(hasChauffage && hasEcs
                ? { ecs_sur_pac: values.boilerForEcs === "yes" }
                : {}),
        };

        // Update json root fields so "Your Installation" step reflects changes
        const currentJson = JSON.parse(etudeData?.json || "{}");
        const updatedJson = {
            ...currentJson,
            t_base: values.t_base,
            Tcoupure: values.Tcoupure,
            TcoupureQuestion: values.TcoupureQuestion,
            t_ete: values.t_ete,
            distance_pac_ballon: values.distance_pac_ballon,
            distance_pac_batiment: values.distance_pac_batiment,
            antiCorrosion: values.antiCorrosion,
            ...(isDoubleServiceHybrid ? { boilerForEcs: values.boilerForEcs } : {}),
            circuits: values.circuits,
            ...(etudeFormSolutions.length > 0
                ? {
                    etudeFormSolutionId: +values.etudeFormSolutionId,
                    etudeFormSolution: etudeFormSolutions[+values.etudeFormSolutionId],
                }
                : {}),
            solutionRequestBody: newSolutionRequestBody,
        };

        dispatch(
            setEtudeData({
                ...etudeData!,
                json: JSON.stringify(updatedJson),
            })
        );

        closeLastPopup();
    }, [
        solutionRequestBody,
        etudeData,
        hasChauffage,
        hasEcs,
        etudeFormSolutions,
        json,
        dispatch,
        closeLastPopup,
    ]);

    return (
        <Popup
            title={t("FO_RESULT_MODIFY_INPUT")}
            translateTitle
            toolbar={
                <group data-space="20" data-justify="end">
                    <Button primary text={t("FO_RESULT_MODIFY_VALIDATE")} onClick={handleSubmit(onValidate)} />
                </group>
            }
            {...props}
        >
            <group data-space="20" data-direction="column" data-gap="20">
                {/* Basic parameters section */}
                <group data-direction="column" data-gap="10">
                    <group
                        data-type="grid"
                        data-grid-template="320"
                        data-gap="10"
                    >
                        <group>
                            <Input
                                label={t("FO_SIMULATION_BASE_TEMPERATURE")}
                                type={InputType.Number}
                                name="t_base"
                                control={control}
                                unit={t("FO_UNIT_C")}
                                min={valeurParDefaut.localisation.t_base.minimum}
                                maxValue={valeurParDefaut.localisation.t_base.maximum}
                                error={t("FO_SIMULATION_BASE_TEMPERATURE_HELP")}
                            />

                        </group>


                        {hasEcs && (
                            <Input
                                label={t("FO_SIMULATION_SUMMER_TEMPERATURE")}
                                type={InputType.Number}
                                name="t_ete"
                                control={control}
                                unit={t("FO_UNIT_C")}
                                minValue={valeurParDefaut.localisation.t_ete.minimum}
                                maxValue={valeurParDefaut.localisation.t_ete.maximum}
                            />
                        )}
                        {isHybride && (
                            <>
                                {tcoupureQuestion && (
                                    <Input
                                        type={InputType.Number}
                                        name="Tcoupure"
                                        control={control}
                                        label={t("FRONT_SIMULATION_TCOUPURE")}
                                        unit={t("FO_UNIT_C")}
                                        minValue={valeurParDefaut.localisation.Tcoupure.minimum}
                                        maxValue={valeurParDefaut.localisation.Tcoupure.maximum}
                                    />
                                )}
                            </>
                        )}


                    </group>
                    {isHybride && (
                        <Input
                            name="TcoupureQuestion"
                            control={control}
                            type="Checkbox"
                            label={t("FRONT_SIMULATION_TCOUPURE_QUESTION")}
                        // style={{ wordWrap: "break-word", overflowWrap: "break-word" }}
                        />
                    )}
                    <group data-type="grid"
                        data-grid-template="320"
                        data-gap="10">

                        <Input
                            label={t("FO_SIMULATION_DISTANCE_PAC_TANK")}
                            type={InputType.Number}
                            name="distance_pac_ballon"
                            control={control}
                            unit={t("FO_UNIT_M")}
                            minValue={valeurParDefaut.installation.distance_pac_ballon.minimum}
                            maxValue={valeurParDefaut.installation.distance_pac_ballon.maximum}
                        />

                        <Input
                            label={t("FO_SIMULATION_DISTANCE_PAC_BUILDING")}
                            type={InputType.Number}
                            name="distance_pac_batiment"
                            control={control}
                            unit={t("FO_UNIT_M")}
                            minValue={valeurParDefaut.installation.distance_pac_batiment.minimum}
                            maxValue={valeurParDefaut.installation.distance_pac_batiment.maximum}
                        />
                        <OptionBar
                            label={t("FO_SIMULATION_ANTI_CORROSION")}
                            control={control}
                            name="antiCorrosion"
                            options={[
                                { value: "yes", text: t("FO_ETU_YES") },
                                { value: "no", text: t("FO_ETU_NO") },
                            ]}
                        />
                        {/* Boiler for ECS - Double Service Hybrid only */}
                        {isDoubleServiceHybrid && (
                            <group data-direction="column" data-gap="10">
                                <OptionBar
                                    label={t("FO_SIMULATION_BOILER_FOR_ECS")}
                                    control={control}
                                    name="boilerForEcs"
                                    options={[
                                        { value: "yes", text: t("FO_ETU_YES") },
                                        { value: "no", text: t("FO_ETU_NO") },
                                    ]}
                                />
                            </group>
                        )}
                    </group>




                </group>

                {/* Heating circuits section */}
                {hasChauffage && (
                    <group data-direction="column" data-gap="10">
                        <group>
                            <Text
                                data-text-size="14"
                                data-color="grey-color-mid"
                                data-weight="600"
                                data-wrap="wrap"
                            >
                                FO_SIMULATION_HEATING_CIRCUITS
                            </Text>
                        </group>
                        <group data-gap="20">
                            <CircuitList
                                control={control}
                                name="circuits"
                                maxCount={5}
                                service={service}
                            />
                        </group>
                    </group>
                )}

                {/* ECS system selection section */}
                {hasEcs && (
                    <group data-direction="column" data-gap="10" data-length="1000">
                        <group>
                            <Text
                                data-text-size="14"
                                data-color="grey-color-mid"
                                data-weight="600"
                                data-wrap="wrap"
                            >
                                FO_SIMULATION_ECS_SELECTED_PRODUCT
                            </Text>
                        </group>
                        {
                            etudeFormSolutions && etudeFormSolutions.length > 0 ? (
                                <SolutionRadio
                                    name="etudeFormSolutionId"
                                    control={control}
                                    systeme={json?.systeme}
                                    options={etudeFormSolutions.map((s: any, i: number) => ({
                                        puissance: s.puissance,
                                        references_commerciales: s.references_commerciales,
                                        value: i.toString(),
                                        optimale: s.optimale,
                                    }))}
                                />
                            ) : null}
                    </group>
                )}
            </group>
        </Popup>
    );
};
